import { createContext, use, useEffect, useMemo, useState, type ReactNode } from 'react'
import { FIXTURES_ENABLED, initKeycloakOnce, keycloak } from './keycloak'
import { returnTo, signedOut } from './session'

interface AuthState {
  ready: boolean
  authenticated: boolean
  /**
   * The refresh token stopped working: the session is over, but the page is
   * still full of data that was true when it loaded.
   *
   * ── ITS ONLY REAL TRIGGER IS THE PERIODIC NUDGE ─────────────────────────
   * `keycloak.onAuthLogout` looks like the obvious second trigger and cannot
   * fire here: it is driven by the login-status iframe, and this app disables
   * that iframe AND ships no silent-check redirect URI — the exact combination
   * that removes it (see the long comment in `keycloak.ts`). So a session
   * revoked server-side is not noticed until the next `updateToken`, and
   * pretending otherwise would be a comment that describes a different app.
   */
  expired: boolean
  /** Keycloak `sub`. Equals developers.users.id, which is what makes the join exact. */
  subject?: string
  login: () => void
  logout: () => void
}

const Ctx = createContext<AuthState>({
  ready: false,
  authenticated: false,
  expired: false,
  login: () => {},
  logout: () => {},
})

/**
 * The SPA's own origin, read once at module load.
 *
 * Deliberately NOT read at call time: see the comment on `login` below.
 */
const APP_ORIGIN = `${window.location.origin}/`

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(FIXTURES_ENABLED)
  const [authenticated, setAuthenticated] = useState(FIXTURES_ENABLED)
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    // Fixture mode still renders the authenticated tree, but skips the network
    // round trip: the point is to look at screens, not to test Keycloak.
    if (FIXTURES_ENABLED) return

    let cancelled = false
    // initKeycloakOnce, never keycloak.init: StrictMode mounts this effect
    // twice and a second init() throws. See the long comment on that function.
    initKeycloakOnce()
      .then((ok) => {
        if (cancelled) return
        setAuthenticated(ok)
        setReady(true)
      })
      .catch((e) => {
        // Logged, not swallowed. The bare `.catch(() => …)` that used to be here
        // is why an init failure presented as a silent redirect loop instead of
        // as an error: there was nothing to read anywhere.
        console.error('[auth] Keycloak init failed', e)
        if (!cancelled) setReady(true)
      })

    // Belt over the request interceptor's updateToken(30). A monitoring wall is
    // a background tab by design, and a throttled interval is exactly what
    // caused the product's 2026-08-13 ExpiredSignature incident. Here the token
    // lives 900 s rather than 86,400, so the interceptor is the real defence and
    // this is only the cheap periodic nudge.
    const t = window.setInterval(() => {
      // The rejection used to be swallowed. It is the ONE observable moment at
      // which this app can learn the session is over, and dropping it is why an
      // expiry presented as a screen full of red panels reading like an outage.
      keycloak.updateToken(60).catch(() => setExpired(true))
    }, 60_000)

    return () => {
      cancelled = true
      window.clearInterval(t)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      ready,
      authenticated,
      subject: FIXTURES_ENABLED ? 'fixture' : keycloak.subject,
      expired,
      // AMBOS redirects se pasan explicitos. keycloak-js, sin `redirectUri`,
      // usa `window.location.href` EN EL MOMENTO DE LA LLAMADA — y cuando
      // StaffOnly llamaba login() en cada render, una de esas llamadas lo leyo
      // mientras el navegador ya estaba navegando a Keycloak. El redirect_uri
      // enviado era entonces la URL de Keycloak, que naturalmente no esta en la
      // whitelist: "Invalid parameter: redirect_uri", despues de parpadear.
      //
      // APP_ORIGIN se captura al cargar el modulo, antes de que exista ninguna
      // navegacion, asi que no puede ser otra cosa que el origen del SPA.
      login: () => {
        // El destino se guarda ANTES de navegar: como `redirectUri` esta fijado
        // al origen a proposito, Keycloak siempre vuelve a `/`, y sin esto cada
        // link profundo que abra alguien sin sesion aterriza en el Resumen.
        returnTo.park(`${window.location.pathname}${window.location.search}`)
        signedOut.clear()
        keycloak.login({ redirectUri: APP_ORIGIN })
      },
      logout: () => {
        // La bandera se pone PRIMERO. Sin ella, `check-sso` no encuentra sesion
        // al volver y el efecto de StaffOnly te vuelve a meter de inmediato.
        signedOut.mark()
        keycloak.logout({ redirectUri: APP_ORIGIN })
      },
    }),
    [ready, authenticated, expired],
  )

  return <Ctx value={value}>{children}</Ctx>
}

export function useAuth(): AuthState {
  return use(Ctx)
}
